import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaLightbulb, 
  FaArrowLeft,
  FaPencilAlt,
  FaMobile,
  FaDesktop,
  FaHandshake,
  FaClipboardCheck
} from 'react-icons/fa';
import './ServicePage.css';
import WhatsappFloat from '../components/WhatsappFloat';
import { Helmet } from 'react-helmet-async';

const Prototipacao = () => {
  const navigate = useNavigate();

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Prototipação | MicroCodie</title>
        <meta name="description" content="Valide ideias com protótipos interativos antes de investir no desenvolvimento. Prototipação rápida e eficiente." />
        <meta property="og:title" content="Prototipação | MicroCodie" />
        <meta property="og:description" content="Valide ideias com protótipos interativos antes de investir no desenvolvimento. Prototipação rápida e eficiente." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.microcodie.com.br/servicos/prototipacao" />
        <meta property="og:image" content="/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Prototipação | MicroCodie" />
        <meta name="twitter:description" content="Valide ideias com protótipos interativos antes de investir no desenvolvimento. Prototipação rápida e eficiente." />
        <meta name="twitter:image" content="/logo192.png" />
        <link rel="canonical" href="https://www.microcodie.com.br/servicos/prototipacao" />
      </Helmet>
      <div className="service-page">
        <div className="service-header">
          <div className="container">
            <motion.button 
              className="back-button"
              onClick={() => navigate('/')}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FaArrowLeft />
              Voltar
            </motion.button>
            
            <motion.div 
              className="service-hero"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="service-icon-large">
                <FaLightbulb />
              </div>
              <h1>Prototipamos ideias antes de você investir pesado nelas</h1>
              <p className="service-subtitle">
                Com protótipos interativos, você valida seu produto e evita desperdícios
              </p>
              <button 
                className="btn btn-primary hero-cta"
                onClick={() => navigate('/#contato')}
              >
                Solicitar orçamento
              </button>
            </motion.div>
          </div>
        </div>

        <div className="service-content">
          <div className="container">
            {/* Seção: O que é prototipação? */}
            <motion.div 
              className="prototyping-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="prototyping-col prototyping-col-left">
                <h2>O que é prototipação?</h2>
                <p>
                  Prototipação é o processo de criar versões simplificadas e interativas do seu produto antes do desenvolvimento completo. 
                  Permite validar ideias, testar funcionalidades e obter feedback real de usuários, economizando tempo e recursos.
                </p>
              </div>
              <div className="prototyping-col prototyping-col-right">
                <div className="prototyping-flow-card">
                  <div className="prototyping-flow-icon"><FaPencilAlt /></div>
                  <div>
                    <h4>Ideação</h4>
                    <p>Transformamos sua ideia inicial em um conceito claro e estruturado.</p>
                  </div>
                </div>
                <div className="prototyping-flow-card">
                  <div className="prototyping-flow-icon"><FaDesktop /></div>
                  <div>
                    <h4>Wireframes</h4>
                    <p>Criamos a estrutura visual básica do seu produto para validação rápida.</p>
                  </div>
                </div>
                <div className="prototyping-flow-card">
                  <div className="prototyping-flow-icon"><FaMobile /></div>
                  <div>
                    <h4>Protótipo funcional</h4>
                    <p>Desenvolvemos uma versão interativa para testes e feedback real.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Seção: Vantagens da prototipação */}
            <motion.div 
              className="advantages-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2>Vantagens da prototipação</h2>
              <div className="advantages-grid">
                <div className="advantage-card">
                  <FaUsers className="advantage-icon" />
                  <h3>Validação com usuários</h3>
                  <p>
                    Teste suas ideias com pessoas reais antes de investir em desenvolvimento. 
                    Descubra o que funciona e o que precisa ser melhorado.
                  </p>
                </div>
                <div className="advantage-card">
                  <FaClipboardCheck className="advantage-icon" />
                  <h3>Clareza de escopo</h3>
                  <p>
                    Defina exatamente o que será desenvolvido, evitando mudanças custosas 
                    durante o processo de desenvolvimento.
                  </p>
                </div>
                <div className="advantage-card">
                  <FaHandshake className="advantage-icon" />
                  <h3>Alinhamento com investidores</h3>
                  <p>
                    Apresente sua ideia de forma visual e interativa para equipes, 
                    investidores ou stakeholders, facilitando a aprovação.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Seção: Exemplos reais */}
            {/* <motion.div 
              className="real-examples-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h2>Exemplos reais</h2>
              <div className="examples-comparison">
                <div className="prototype-side">
                  <h3>Problema identificado no protótipo</h3>
                  <div className="video-container">
                    <div className="video-placeholder">
                      <FaPlay className="play-icon" />
                      <span>Vídeo: Navegação no protótipo</span>
                    </div>
                  </div>
                  <p>
                    Durante os testes, descobrimos que os usuários não conseguiam encontrar 
                    a funcionalidade principal. Isso nos permitiu corrigir antes do desenvolvimento.
                  </p>
                </div>
                <div className="product-side">
                  <h3>Do protótipo ao produto</h3>
                  <div className="product-showcase">
                    <div className="product-image">
                      <img 
                        src="https://via.placeholder.com/400x300/00AFEF/FFFFFF?text=App+Final" 
                        alt="App final desenvolvido"
                      />
                    </div>
                  </div>
                  <p>
                    Com base nos feedbacks do protótipo, desenvolvemos uma interface 
                    mais intuitiva que resolveu o problema identificado.
                  </p>
                </div>
              </div>
            </motion.div> */}

            {/* Seção: Processo */}
            <motion.div 
              className="prototyping-process-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <div className="process-timeline">
                <div className="process-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Reunião inicial</h3>
                    <p>Entendimento da ideia, objetivos e público-alvo</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>Wireframes</h3>
                    <p>Criação da estrutura básica e fluxo de navegação</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Protótipos navegáveis</h3>
                    <p>Desenvolvimento de versões interativas funcionais</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h3>Testes</h3>
                    <p>Validação com usuários reais e coleta de feedback</p>
                  </div>
                </div>
                <div className="process-step">
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <h3>Validação</h3>
                    <p>Análise dos resultados e refinamento do conceito</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Seção: CTA Final */}
            <motion.div 
              className="final-cta-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <h2>Pronto para transformar sua ideia em algo real?</h2>
              <p>
                Não deixe sua ideia apenas no papel. Transforme-a em um protótipo funcional 
                que vai validar seu conceito e orientar o desenvolvimento de forma eficiente.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/#contato')}
              >
                Solicitar orçamento
              </button>
            </motion.div>
          </div>
        </div>
        <WhatsappFloat />
      </div>
    </>
  );
};

export default Prototipacao; 